import { useCallback } from "react";
import { Card, CardBody, Col, Row } from "reactstrap";
import IconComponent from "../../../../CommonElements/IconComponent";
import { Badges, P } from "../../../../AbstractElements";
import {
  attendanceCorrectionModal,
  danger,
  infoColor,
  InLocation,
  InTime,
  light,
  NA,
  OutLocation,
  OutTime,
  Remark,
  secondary,
  success,
  warningColor,
  WorkingHours,
} from "../../../../utils/Constant";
import { convertTo12HourFormat } from "../../../../utils/timeUtils";
import { getTimeDifference } from "../../../../utils/timeUtils";
import useUser from "../../../../hooks/useUser";
import { ModalType } from "../../../../Types/UserProfileDetails";
import { openModal } from "../../../../ReduxToolkit/Slice/UserProfileDetailsModalSlice";
import {
  IAttendanceDetailCardProps,
  IAttendanceDetailsResponse,
} from "../../types";
import { fetchAssginedManagerToEmployee } from "../../../hrZone/Users/redux/slice/AssignedManagerToEmployeeSlice";
import SkeletonLoader from "../../../../CommonElements/Loader/SkeletonLoader";
import { useInViewPort } from "../../../../hooks/useInViewport";

const AttendanceBadgeDetails = ({
  color,
  inView,
  className,
  badgeHeading,
  text,
  loading,
  index,
}: {
  index: number;
  inView: boolean;
  loading?: boolean;
  color: string;
  className: string;
  badgeHeading: string;
  text: string;
}) => {
  return (
    <>
      <Badges
        color={color}
        className={`text-capitalize b-r-4 badge ${className} `}
      >
        {badgeHeading}
      </Badges>
      {loading ? (
        <SkeletonLoader className="mt-2" />
      ) : (
        <P
          className="mb-0 f-12 ms-1 mt-1"
        >
          {text}
        </P>
      )}
    </>
  );
};

const AttendanceDetailsCard = ({
  index,
  dayName,
  dispatch,
  attendanceLoading,
  employeeAttendanceDetails,
}: IAttendanceDetailCardProps) => {
  const user = useUser();
  const { observerRef, inView } = useInViewPort<HTMLDivElement>({
    threshold: 0.1,
    triggerOnce: true,
  });

  // Function to handle modal opening
  const handleOpenModal = useCallback(
    (modalKey: ModalType, selectedId?: string | number | null): void => {
      dispatch(openModal({ modalType: modalKey, selectedId })); // Dispatch modal open action
    },
    [dispatch]
  );

  // Function to handle edit button click
  const handleEditClick = useCallback(
    (row: IAttendanceDetailsResponse): void => {
      // Fetch assigned manager details and open the modal
      dispatch(
        fetchAssginedManagerToEmployee({
          id: Number(row.user_id),
        })
      );
      handleOpenModal(attendanceCorrectionModal, row.date);
    },
    [dispatch, user?.id, handleOpenModal]
  );

  return (
    <Col xs="12" sm="6" key={index}>
      <Card
        innerRef={observerRef}
        className={`notification-tabs mt-2 mb-2 ${
          inView ? "fadeInUp animate" : "opacity-0"
        }`}
      >
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <p className="m-0">
              {dayName}, {employeeAttendanceDetails?.date?.slice(8, 10)}
            </p>
            {employeeAttendanceDetails.editable && (
              <div
                className="b-r-50 d-flex justify-content-center align-items-center alert-light-info pointer"
                style={{ width: "30px", height: "30px" }}
                onClick={() => handleEditClick(employeeAttendanceDetails)}
              >
                <IconComponent className="icofont icofont-edit-alt f-12 text-dark" />
              </div>
            )}
          </div>
          <div className="d-flex justify-content-between mb-3">
            <div>
              <AttendanceBadgeDetails
                color={success}
                className="badget-success"
                badgeHeading={InTime}
                text={convertTo12HourFormat(
                  employeeAttendanceDetails.in_time ?? ""
                )}
                loading={attendanceLoading}
                inView={inView}
                index={index}
              />
            </div>
            <div>
              <AttendanceBadgeDetails
                color={infoColor}
                className="badget-info"
                badgeHeading={OutTime}
                text={convertTo12HourFormat(
                  employeeAttendanceDetails.out_time ?? ""
                )}
                loading={attendanceLoading}
                inView={inView}
                index={index}
              />
            </div>
            <div className="position-relative">
              <AttendanceBadgeDetails
                color={secondary}
                className="badget-info"
                badgeHeading={WorkingHours}
                text={getTimeDifference(
                  employeeAttendanceDetails.in_time ?? "",
                  employeeAttendanceDetails.out_time ?? ""
                )}
                loading={attendanceLoading}
                inView={inView}
                index={index}
              />
              <div
                className="position-absolute"
                style={{ top: "25px", right: "25px" }}
              >
                {employeeAttendanceDetails.in_time &&
                  employeeAttendanceDetails.out_time &&
                  getTimeDifference(
                    employeeAttendanceDetails.in_time ?? "",
                    employeeAttendanceDetails.out_time ?? ""
                  ) <= "9:00" && (
                    <IconComponent className="fa fa-flag f-14 text-danger" />
                  )}
              </div>
            </div>
          </div>
          <Row className="g-2 mb-3">
            {([
              ["expected_hours", "Expected Hours"],
              ["logged_hours", "Logged Hours"],
              ["deficit_hours", "Deficit Hours"],
            ] as const).map(([key, label]) => (
              <Col xs="4" key={key}>
                <P className="m-0 f-12">{label}</P>
                {attendanceLoading ? (
                  <SkeletonLoader className="mt-1" />
                ) : (
                  <P className="m-0 f-12 fw-semibold">{employeeAttendanceDetails[key] != null ? `${employeeAttendanceDetails[key]} hrs` : NA}</P>
                )}
              </Col>
            ))}
          </Row>
          <div className="mb-3">
            <AttendanceBadgeDetails
              color={warningColor}
              className="badget-warning"
              badgeHeading={InLocation}
              text={employeeAttendanceDetails.in_location ?? NA}
              loading={attendanceLoading}
              inView={inView}
              index={index}
            />
          </div>
          <div className="mb-3">
            <AttendanceBadgeDetails
              color={light}
              className="badget-light text-dark"
              badgeHeading={OutLocation}
              text={employeeAttendanceDetails.out_location ?? NA}
              loading={attendanceLoading}
              inView={inView}
              index={index}
            />
          </div>
          <div className="mb-3">
            <AttendanceBadgeDetails
              color={danger}
              className="badget-danger"
              badgeHeading={Remark}
              text={employeeAttendanceDetails.remark_all ?? NA}
              loading={attendanceLoading}
              inView={inView}
              index={index}
            />
          </div>
        </CardBody>
      </Card>
    </Col>
  );
};

export default AttendanceDetailsCard;
